import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Wallet, TrendingUp, TrendingDown, ShieldAlert, Save } from "lucide-react";
import { toast } from "sonner";
import RealizedTrades from "@/components/RealizedTrades";

const STATUS_COLOR = { "PICK DEBT": "var(--negative)", LOADED: "var(--accent)", BALANCED: "var(--text-3)" };

function money(n) { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString()}`; }

export default function Ledger() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [pickLeague, setPickLeague] = useState("");
  const [pickLedger, setPickLedger] = useState(null);

  const load = () => api.get("/ledger").then(r => {
    setData(r.data);
    const dr = {};
    (r.data.synced_leagues || []).forEach(l => {
      const e = (r.data.entries || []).find(x => x.league_id === l.league_id);
      dr[l.league_id] = { season: e?.season || l.season, entry_fee: e?.entry_fee ?? "", winnings: e?.winnings ?? "" };
    });
    setDraft(dr);
    if (!pickLeague && r.data.synced_leagues?.length) setPickLeague(r.data.synced_leagues[0].league_id);
  });
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (pickLeague) { setPickLedger(null); api.get(`/leagues/${pickLeague}/pick-ledger`).then(r => setPickLedger(r.data)); } }, [pickLeague]);

  const save = async (lid) => {
    const d = draft[lid];
    await api.post("/ledger", { league_id: lid, season: String(d.season), entry_fee: Number(d.entry_fee || 0), winnings: Number(d.winnings || 0) });
    toast.success("Ledger saved");
    load();
  };

  if (data === null) return <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;
  const s = data.summary;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ledger-page">
      <div className="flex items-center gap-3 mb-1">
        <Wallet className="w-6 h-6 text-[var(--accent)]" />
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">League Ledger</h1>
      </div>
      <p className="text-sm text-[var(--text-2)] mb-6 max-w-2xl">Your fantasy football financial command center — career ROI across every league, plus Future Pick Debt enforcement.</p>

      {/* ROI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="roi-summary">
        {[
          { k: "INVESTED", v: money(s.total_invested), c: "var(--text)" },
          { k: "WON", v: money(s.total_won), c: "var(--accent)" },
          { k: "NET", v: money(s.net), c: s.net >= 0 ? "var(--accent)" : "var(--negative)" },
          { k: "CAREER ROI", v: `${s.roi_pct}%`, c: s.roi_pct >= 0 ? "var(--accent)" : "var(--negative)" },
        ].map(card => (
          <div key={card.k} className="card-bordered p-4">
            <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">{card.k}</div>
            <div className="font-mono text-2xl" style={{ color: card.c }}>{card.v}</div>
          </div>
        ))}
      </div>

      {/* Financial ledger (manual) */}
      <div className="text-[var(--accent)] text-xs tracking-widest mb-3 font-mono">// ENTRY FEES & PAYOUTS (MANUAL)</div>
      <div className="card-bordered overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[9px] tracking-widest text-[var(--text-3)] font-mono border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">LEAGUE</th><th className="px-3">SEASON</th><th className="px-3">ENTRY FEE</th><th className="px-3">WINNINGS</th><th className="px-3">NET</th><th className="px-3"></th>
            </tr>
          </thead>
          <tbody>
            {(data.synced_leagues || []).map(l => {
              const d = draft[l.league_id] || {};
              const net = Number(d.winnings || 0) - Number(d.entry_fee || 0);
              return (
                <tr key={l.league_id} className="border-b border-[var(--border)] last:border-0" data-testid={`ledger-row-${l.league_id}`}>
                  <td className="px-4 py-2.5 font-semibold truncate max-w-[180px]">{l.name}</td>
                  <td className="px-3"><input className="input-dark w-20 text-center py-1" value={d.season || ""} onChange={e => setDraft({ ...draft, [l.league_id]: { ...d, season: e.target.value } })} data-testid={`fee-season-${l.league_id}`} /></td>
                  <td className="px-3"><input className="input-dark w-24 text-center py-1 font-mono" type="number" value={d.entry_fee} onChange={e => setDraft({ ...draft, [l.league_id]: { ...d, entry_fee: e.target.value } })} data-testid={`fee-input-${l.league_id}`} /></td>
                  <td className="px-3"><input className="input-dark w-24 text-center py-1 font-mono" type="number" value={d.winnings} onChange={e => setDraft({ ...draft, [l.league_id]: { ...d, winnings: e.target.value } })} data-testid={`win-input-${l.league_id}`} /></td>
                  <td className="px-3 text-center font-mono" style={{ color: net >= 0 ? "var(--accent)" : "var(--negative)" }}>{money(net)}</td>
                  <td className="px-3 text-center"><button onClick={() => save(l.league_id)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`ledger-save-${l.league_id}`}><Save className="w-3 h-3" />Save</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Realized trade P&L */}
      <div className="mb-8">
        <RealizedTrades leagues={data.synced_leagues || []} />
      </div>

      {/* Future Pick Debt */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[var(--accent)] text-xs tracking-widest font-mono flex items-center gap-2">// FUTURE PICK DEBT</div>
        <select className="input-dark text-sm py-1.5 w-auto" value={pickLeague} onChange={e => setPickLeague(e.target.value)} data-testid="pickdebt-league">
          {(data.synced_leagues || []).map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
        </select>
      </div>
      {pickLedger === null ? (
        <div className="card-bordered p-8 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>
      ) : (
        <div className="card-bordered overflow-hidden" data-testid="pickdebt-table">
          <div className="px-4 py-3 border-b border-[var(--border)] text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[var(--amber)]" />
            <span className="text-[var(--text-2)]">{pickLedger.debt_count} team{pickLedger.debt_count !== 1 ? "s" : ""} carrying future pick debt</span>
          </div>
          {pickLedger.rows.map(r => (
            <div key={r.roster_id} className={`grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0 ${r.is_user ? "bg-[rgba(0,255,65,0.04)]" : ""}`} data-testid={`pickdebt-${r.roster_id}`}>
              <div className="col-span-4 truncate font-semibold">{r.manager}{r.is_user && <span className="text-[9px] text-[var(--accent)] ml-2 font-mono">YOU</span>}</div>
              <div className="col-span-2 font-mono" style={{ color: r.net >= 0 ? "var(--accent)" : "var(--negative)" }}>{r.net > 0 ? "+" : ""}{r.net}</div>
              <div className="col-span-3 text-xs text-[var(--text-3)]">{r.acquired.length} in · {r.traded_away.length} out{r.firsts_owed ? ` · owes ${r.firsts_owed} 1st` : ""}</div>
              <div className="col-span-3 text-right">
                <span className="text-[10px] tracking-widest font-mono px-2 py-0.5 rounded-sm border" style={{ color: STATUS_COLOR[r.status], borderColor: STATUS_COLOR[r.status] + "66" }}>{r.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
